import { AuthServerProvider as AuthSession } from "./provider/auth.session.service";
import { TokenProviderService } from "src/app/api";
import { preserveItems, restorePreserved } from "../util/local-storage-preserve.service";
import {
  emRetornoDeLogin,
  encerrarSessaoNestaAba,
  irParaTelaDeLogin,
  isKeycloakSession,
  tokenRecusadoNestaAba,
} from "./keycloak/keycloak.util";

export class AuthService {
  private _currentUser = null;

  private get currentUser() {
    return this._currentUser;
  }

  private set currentUser(user) {
    if (!user) {
      this._currentUser = null;
      return;
    }

    this._currentUser = {
      email: user.email,
      name: user.nome,
      role: user.role,
      id: user.id,
      isPj: user.pj,
      emailIndicador: user.emailIndicador,

      codigoCompartilhamento: user.codigoCompartilhamento,
      oferta: user.oferta,
      canal: user.canal,
      saldo: user.saldo,
      valorConvidado: user.valorConvidado,
    };
  }

  constructor(
    private localStorageService,
    private AuthServerProvider: AuthSession,
    private Pedido,
    private Principal,
    private $appTokenProvider: TokenProviderService
  ) {
    "ngInject";

    const currentUser = localStorageService.get("user");
    if (currentUser) {
      this.currentUser = currentUser;
      this.$appTokenProvider.currentUser = currentUser;
    }
  }

  /**
   * Authenticate user
   *
   * @param user - login info
   */
  async login(user) {
    const data = await this.AuthServerProvider.login(user);

    this.currentUser = data.data;

    this.$appTokenProvider.currentUser = data.data;

    return data;
  }

  /**
   * Conclui o login pelo Keycloak (retorno do redirect com ?code=&state=).
   */
  async loginKeycloak(search: string) {
    return this.AuthServerProvider.loginKeycloak(search);
  }

  /**
   * Delete access token and user info
   */
  async logout(eventos) {
    // Faz logout se existe o token no localStorage
    if (this.localStorageService.get("token")) {
      const data = await this.AuthServerProvider.logout();

      this.currentUser = null;
      this.$appTokenProvider.currentUser = null;
      this.Pedido.novoPedido();

      this.Principal.authenticate(null);

      return data;
    }

    return null;
  }

  /**
   * Gets all available info on authenticated user
   */
  getCurrentUser() {
    return this.currentUser || {};
  }

  /**
   * Check if a user is logged in. The stored user alone is not a session: without a token (never
   * signed in, wiped by a 401, or given up on in this tab) or while the Keycloak sign-in is still being
   * completed, the shell must not load customer data, or those calls go out unauthenticated and come
   * back 401.
   */
  isLoggedIn() {
    return (
      !!this.currentUser &&
      this.currentUser.hasOwnProperty("role") &&
      !!this.localStorageService.get("token") &&
      !tokenRecusadoNestaAba() &&
      !emRetornoDeLogin()
    );
  }

  /**
   * Waits for currentUser to resolve before checking if user is logged in
   */
  async isLoggedInAsync() {
    if (this.currentUser && this.currentUser.hasOwnProperty("$promise")) {
      try {
        return !!(await this.currentUser.$promise);
      } catch (e) {
        return false;
      }
    }

    try {
      await this.AuthServerProvider.authorize();
      return true;
    } catch (err) {
      this.currentUser = null;
      if (isKeycloakSession()) {
        // Only in this tab: the stored session is shared, and other tabs may still renew it.
        encerrarSessaoNestaAba();
      } else {
        const items = preserveItems();
        this.localStorageService.clearAll();
        restorePreserved(items);
      }
      // The session is over (expired Keycloak token that could not be renewed, or a legacy one): sign in
      // again instead of leaving the page open with no user and every call coming back 401.
      irParaTelaDeLogin();
      return false;
    }
  }

  /**
   * Check if a user is an admin
   */
  isAdmin() {
    return this.currentUser && this.currentUser.role === "admin";
  }

  /**
   * Get auth token
   */
  getToken() {
    return this.localStorageService.get("token");
  }

  /**
   * Get auth PJ
   */
  isPJ() {
    const user = this.localStorageService.get("user");

    if (user) {
      return user.pj;
    }

    return null;
  }

  /**
   * Get auth PJ
   */
  setPJ(pj) {
    const user = this.localStorageService.get("user") || {};
    user.pj = pj;
    this.localStorageService.set("user", user);
  }
}
